import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, Phone, X } from "lucide-react";
import AppButton from "@/components/AppButtons";
import { SITE } from "@/config/site";
import useSectionNav from "@/hooks/useSectionNav";

const LINKS = [
  { label: "Home", to: "/" },
  { label: "Services", id: "services" },
  { label: "Why Postings", id: "why" },
  { label: "About", id: "about" },
  { label: "Contact", to: "/contact" },
];

const slug = (label) => label.toLowerCase().replace(/\s+/g, "-");

export default function Header() {
  const [open, setOpen] = useState(false);
  const go = useSectionNav();

  useEffect(() => {
    if (!window.__lenis) return undefined;
    if (open) window.__lenis.stop();
    else window.__lenis.start();
    return undefined;
  }, [open]);

  const handle = (item) => {
    setOpen(false);
    go(item);
  };

  return (
    <header className="fixed inset-x-0 top-0 z-40 border-b border-ink/5 bg-white/70 backdrop-blur-xl">
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between gap-4 px-6 lg:px-8">
        <Link
          to="/"
          onClick={() => setOpen(false)}
          data-testid="header-logo-link"
          className="flex items-center"
          aria-label="Postings Inc. home"
        >
          <img
            src="/postings-logo.png"
            alt="Postings Inc. — bookkeeping & payroll"
            width="600"
            height="241"
            className="h-11 w-auto"
          />
        </Link>

        <nav className="hidden items-center gap-7 lg:flex" aria-label="Primary">
          {LINKS.map((l) => (
            <button
              key={l.label}
              onClick={() => handle(l)}
              data-testid={`header-nav-${slug(l.label)}`}
              className="text-sm font-medium text-slate-600 transition-colors hover:text-ink"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="hidden items-center gap-2.5 lg:flex">
          <AppButton app={SITE.apps.clientManager} variant="outline" size="sm" testid="header-app-client-manager" />
          <AppButton app={SITE.apps.postWage} variant="solid" size="sm" testid="header-app-postwage" />
        </div>

        <button
          className="grid h-11 w-11 place-items-center rounded-full border border-ink/10 bg-white/80 text-ink lg:hidden"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-label={open ? "Close menu" : "Open menu"}
          data-testid="header-mobile-toggle"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="border-t border-ink/5 bg-white/95 backdrop-blur-xl lg:hidden"
            data-testid="mobile-menu"
          >
            <nav className="space-y-1 px-6 py-6" aria-label="Mobile">
              {LINKS.map((l) => (
                <button
                  key={l.label}
                  onClick={() => handle(l)}
                  data-testid={`mobile-nav-${slug(l.label)}`}
                  className="block w-full rounded-xl px-3 py-3 text-left font-cabinet text-2xl font-bold tracking-tight text-ink transition-colors hover:bg-surface"
                >
                  {l.label}
                </button>
              ))}
              <div className="mt-5 grid gap-3 border-t border-ink/5 pt-5">
                <AppButton app={SITE.apps.clientManager} variant="solid" size="lg" className="w-full" testid="mobile-app-client-manager" />
                <AppButton app={SITE.apps.postWage} variant="ink" size="lg" className="w-full" testid="mobile-app-postwage" />
              </div>
              <a
                href={SITE.phoneHref}
                data-testid="mobile-menu-phone"
                className="mt-4 flex items-center justify-center gap-2 py-2 text-sm font-semibold text-slate-600"
              >
                <Phone className="h-4 w-4" aria-hidden="true" /> {SITE.phoneDisplay}
              </a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
